import { Team } from '@internal/schemas-common';
import { useMemo } from 'react';

import {
  getRenovateScore,
  useGetMergeRequests,
} from '@internal/backstage-plugin-renovate-stats';
import {
  getSecretsScore,
  useGetSecrets,
} from '@internal/backstage-plugin-aws-services';
import {
  getVulnerabilityScore,
  useGetVulnerabilities,
} from '@internal/backstage-plugin-zf-vulnerabilities';

export function useTeamData(team?: Team) {
  const teams = team ? [team] : [];

  const { value: mergeRequests, loading: mergeRequestsLoading } =
    useGetMergeRequests({ teams });
  const {
    vulnerabilities: vulnerabilitiesStats,
    loading: vulnerabilitiesLoading,
  } = useGetVulnerabilities({ teams });
  const { value: allSecrets, loading: secretsLoading } = useGetSecrets({
    teams,
  });

  const renovateData = useMemo(() => {
    if (mergeRequestsLoading) return undefined;
    return getRenovateScore(mergeRequests);
  }, [mergeRequests, mergeRequestsLoading]);

  const vulnerabilitiesData = useMemo(() => {
    if (vulnerabilitiesLoading) return undefined;
    return getVulnerabilityScore(vulnerabilitiesStats);
  }, [vulnerabilitiesStats, vulnerabilitiesLoading]);

  const secretsData = useMemo(() => {
    if (secretsLoading) return undefined;
    return getSecretsScore(allSecrets ?? []);
  }, [allSecrets, secretsLoading]);

  const longestOutdatedSecret = useMemo(() => {
    if (secretsLoading) return -1;
    return (allSecrets ?? []).reduce(
      (oldest, secret) => Math.max(oldest, secret.ChangedSince || 0),
      0,
    );
  }, [allSecrets, secretsLoading]);

  const isLoading =
    mergeRequestsLoading || vulnerabilitiesLoading || secretsLoading;

  return {
    renovateData,
    vulnerabilitiesData,
    secretsData,
    longestOutdatedSecret,
    isLoading,
  };
}
